import React, { createContext, useContext, useState, useEffect } from 'react';
import { isGolfOSVariant } from '../config/branding';

export interface FeatureFlags {
  dashboard: boolean;
  forms: boolean;
  crm: {
    leads: boolean;
    opportunities: boolean;
    customers: boolean;
  };
  sales: {
    estimates: boolean;
    invoices: boolean;
    payments: boolean;
  };
  operations: {
    calendar: boolean;
    routes: boolean;
  };
  assets: {
    vehicles: boolean;
    drivers: boolean;
  };
  configuration: {
    settings: boolean;
  };
}

interface FeatureFlagsContextType {
  flags: FeatureFlags;
  loading: boolean;
  error: string | null;
  refreshFlags: () => Promise<void>;
}

interface FeatureFlagsProviderProps {
  children: React.ReactNode;
}

const distillDefaultFlags: FeatureFlags = {
  dashboard: true,
  forms: true,
  crm: {
    leads: true,
    opportunities: true,
    customers: true,
  },
  sales: {
    estimates: true,
    invoices: true,
    payments: true,
  },
  operations: {
    calendar: true,
    routes: false,
  },
  assets: {
    vehicles: true,
    drivers: true,
  },
  configuration: {
    settings: true,
  },
};

const golfOSDefaultFlags: FeatureFlags = {
  dashboard: true,
  forms: true,
  crm: {
    leads: false,
    opportunities: false,
    customers: false,
  },
  sales: {
    estimates: false,
    invoices: false,
    payments: false,
  },
  operations: {
    calendar: true,
    routes: false,
  },
  assets: {
    vehicles: true,
    drivers: false,
  },
  configuration: {
    settings: true,
  },
};

const defaultFlags: FeatureFlags = isGolfOSVariant ? golfOSDefaultFlags : distillDefaultFlags;

const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(undefined);

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagsContext);
  if (context === undefined) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
  }
  return context;
};

export const useSafeFeatureFlags = () => {
  try {
    return useFeatureFlags();
  } catch {
    return {
      flags: defaultFlags,
      loading: false,
      error: null,
      refreshFlags: async () => {},
    };
  }
};

export const FeatureFlagsProvider: React.FC<FeatureFlagsProviderProps> = ({ children }) => {
  const [flags, setFlags] = useState<FeatureFlags>(defaultFlags);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFeatureFlags = async () => {
    try {
      setLoading(true);
      setError(null);

      // Import the service dynamically to avoid circular dependencies
      const { featureFlagsService } = await import('../services/featureFlagsService');
      const response = await featureFlagsService.getFeatureFlags();

      // Merge with defaults to ensure all flags exist
      const mergedFlags = { ...defaultFlags, ...response.flags };
      setFlags(mergedFlags);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      // Keep default flags on error
      setFlags(defaultFlags);
    } finally {
      setLoading(false);
    }
  };

  const refreshFlags = async () => {
    await fetchFeatureFlags();
  };

  useEffect(() => {
    fetchFeatureFlags();
  }, []);

  const value: FeatureFlagsContextType = {
    flags,
    loading,
    error,
    refreshFlags,
  };

  return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>;
};
